import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./registerScreen.module.css";
import { PublicFooter } from "../ui/footer/publicFooter.component";
import { CopigoLogo } from "../ui/copigoLogo/copigoLogo.component";
import { Box, Button, Checkbox, FormControl, FormControlLabel, IconButton, TextField, Tooltip, Typography, Link, RadioGroup, Radio, FormLabel } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSubscriptionTariffsRequests } from "../../api/subscription_tariffs";
import { SubscriptionTariff } from "../../types/subscriptions";
import { useTenantsRequests } from "../../api/tenants";
import { useLegalDocumentsRequests } from "../../api/legal";
import { LegalDocument, LegalDocumentKind } from "../../types/legal";
import { DOCUMENT_LABELS } from "../ui/legalLinks/legalLinks.component";
import { DatePickerLocalizationProvider } from "../ui/useDatePickerLocalization";
import { DATE_TIME_PICKER_FORMAT } from "../ui/datePickerFormats";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, REGISTER_PLAN_QUERY_PARAM, SUCCESS_SNACKBAR } from "../../consts";
import { formatNumber, formatQuantity } from "../../utils";

// Each checkbox binds its own group of documents. They stay separate on
// purpose: bundling the marketing consent with the terms would invalidate it,
// and the privacy policy is an acknowledgement rather than a consent.
// Anything requiring confirmation that isn't named here lands in "privacy",
// so publishing a new document kind can never silently break registration.
const CONSENT_GROUPS: { key: string; kinds: LegalDocumentKind[] }[] = [
  { key: "terms", kinds: ["TOS", "SLA"] },
  { key: "dpa", kinds: ["DPA"] },
  { key: "privacy", kinds: ["PRP", "UNO"] },
];

const NAMED_KINDS = CONSENT_GROUPS.flatMap((group) => group.kinds);

// Rendered as "<prefix> <link>, <link>", so a group works whatever number of
// documents happens to be published under it.
const CONSENT_PREFIXES: Record<string, string> = {
  terms: "I have read and accept:",
  dpa: "I conclude the following on behalf of my company:",
  privacy: "I confirm I have read:",
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidPhone = (value: string) => {
  if (!/^\+?[\d\s().-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};
const isValidContact = (value: string) => {
  const trimmed = value.trim();
  return isValidEmail(trimmed) || isValidPhone(trimmed);
};

// DRF hands back field errors in two shapes: a list `[{message}]` from its
// built-in validators, and a bare `{message}` from our custom serializer
// checks (e.g. a duplicate workspace name). Accept either so a server-side
// validation error never crashes the form.
const fieldErrorMessage = (fieldError: any): string | undefined =>
  Array.isArray(fieldError) ? fieldError[0]?.message : fieldError?.message;

const InfoHint = ({ text }: { text: string }) => (
  <Tooltip title={text}>
    <InfoOutlinedIcon
      fontSize="small"
      sx={{ color: "text.secondary", cursor: "help", ml: 0.5, verticalAlign: "middle" }}
    />
  </Tooltip>
);

export const RegisterScreen = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { query } = useSubscriptionTariffsRequests();
  const { query: queryLegalDocuments } = useLegalDocumentsRequests();
  const { loading, create, error } = useTenantsRequests();

  const [registerData, setRegisterData] = useState({
    email: "",
    tenantName: "",
    subscriptionTariff: "",
  });
  const [subscriptionTariffs, setSubscriptionTariffs] = useState<SubscriptionTariff[]>([]);
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);
  const [acceptedGroups, setAcceptedGroups] = useState<Record<string, boolean>>({});
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [inquiryContact, setInquiryContact] = useState("");
  const [inquiryWhen, setInquiryWhen] = useState<Dayjs | null>(null);
  const [errors, setErrors] = useState({
    subscriptionTariff: "",
    legal: "",
  });

  useEffect(() => {
    query().then((data) => {
      const tariffs = data?.results || [];
      setSubscriptionTariffs(tariffs);

      // The landing page hands the plan a visitor picked there over as
      // ?plan=<tariff id>. Only preselect it if it is still on offer, so a
      // stale or hand-edited link falls back to an empty choice rather than
      // to a plan that cannot be registered.
      const requestedTariff = searchParams.get(REGISTER_PLAN_QUERY_PARAM);
      if (requestedTariff && tariffs.some((tariff) => String(tariff.id) === requestedTariff)) {
        setRegisterData((prev) => ({ ...prev, subscriptionTariff: requestedTariff }));
      }
    });
    queryLegalDocuments().then((data) => setLegalDocuments(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only documents the backend actually gates registration on.
  const requiredDocuments = useMemo(
    () => legalDocuments.filter((document) => document.requires_action),
    [legalDocuments]
  );

  // Only render a checkbox for a group whose documents are actually published.
  const visibleGroups = useMemo(() => {
    const unnamed = requiredDocuments.filter(
      (document) => !NAMED_KINDS.includes(document.kind)
    );

    return CONSENT_GROUPS.map((group) => ({
      ...group,
      documents: [
        ...requiredDocuments.filter((document) => group.kinds.includes(document.kind)),
        ...(group.key === "privacy" ? unnamed : []),
      ],
    })).filter((group) => group.documents.length > 0);
  }, [requiredDocuments]);

  const allGroupsAccepted = visibleGroups.every((group) => acceptedGroups[group.key]);

  const selectedTariff = subscriptionTariffs.find(
    (tariff) => String(tariff.id) === registerData.subscriptionTariff
  );
  const isEnterprise = Boolean(selectedTariff?.is_custom_priced);
  const trialDays = subscriptionTariffs[0]?.trial_duration_days;
  const vatExempt = subscriptionTariffs.some((tariff) => tariff.vat_exempt);
  const vatPercentRaw = subscriptionTariffs.find((tariff) => tariff.vat_percent)?.vat_percent;
  const vatPercent = vatPercentRaw && !vatExempt ? formatQuantity(vatPercentRaw) : null;

  const contactValid = isValidContact(inquiryContact);
  const whenValid = !inquiryWhen || inquiryWhen.isValid();
  const inquiryReady = !isEnterprise || (contactValid && whenValid);

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData((prev) => ({ ...prev, [name]: value }));
    if (name === "subscriptionTariff" && value) {
      setErrors((prev) => ({ ...prev, subscriptionTariff: "" }));
    }
  };

  const handleGroupToggle = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptedGroups((prev) => ({ ...prev, [key]: e.target.checked }));
    setErrors((prev) => ({ ...prev, legal: "" }));
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!registerData.subscriptionTariff) {
      setErrors((prev) => ({
        ...prev,
        subscriptionTariff: t("Please choose a subscription plan."),
      }));
      return;
    }

    if (!allGroupsAccepted) {
      setErrors((prev) => ({
        ...prev,
        legal: t("You have to accept the documents required to create an account."),
      }));
      return;
    }

    if (!inquiryReady) {
      return;
    }

    const acceptedDocumentIds = visibleGroups
      .flatMap((group) => group.documents)
      .map((document) => document.id);

    const { success } = await create({
      name: registerData.tenantName,
      email: registerData.email,
      subscription_tariff: registerData.subscriptionTariff,
      accepted_document_ids: acceptedDocumentIds,
      marketing_consent: marketingConsent,
      ...(isEnterprise
        ? {
            enterprise_inquiry: {
              contact: inquiryContact.trim(),
              ...(inquiryWhen && inquiryWhen.isValid()
                ? { preferred_contact_at: inquiryWhen.toISOString() }
                : {}),
            },
          }
        : {}),
    });
    if (success) {
      navigate('/login');
      enqueueSnackbar(t("Registration successful. Please login to continue."), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Registration failed. Please try again."), ERROR_SNACKBAR);
    }
  };

  const renderConsentLabel = (documents: LegalDocument[], labelKey: string) => (
    <>
      {t(CONSENT_PREFIXES[labelKey])}{" "}
      {documents.map((document, index) => (
        <React.Fragment key={document.id}>
          {index > 0 && ", "}
          <Link
            href={document.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {t(DOCUMENT_LABELS[document.kind])}
          </Link>
        </React.Fragment>
      ))}
    </>
  );

  const renderTariffLabel = (tariff: SubscriptionTariff) => (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography variant="body2">{tariff.name}</Typography>
      {tariff.is_custom_priced ? (
        <Typography variant="caption" color="text.secondary">
          {t("Individual pricing")}
          <InfoHint text={t("We will contact you to discuss pricing.")} />
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {formatNumber(tariff.price_net)} {t("/ month")}
          {vatExempt ? "" : ` ${t("net")}`}
        </Typography>
      )}
      {tariff.equipment_limit !== null && (
        <Typography variant="caption" color="text.secondary">
          {t("Equipment limit")}: {tariff.equipment_limit}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box className={styles.container}>
      <CopigoLogo className={styles.brandLogo} />
      <Box className={styles.registerBox}>
        <form onSubmit={handleRegisterSubmit}>
          <FormControl variant="standard" className={styles.formControl} fullWidth>
            <div className={styles.headerRow}>
              <IconButton
                className={styles.backButton}
                onClick={() => navigate('/')}
                aria-label={t("Go back")}
                size="small"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <div className={styles.headerTitle}>{t("Register")}</div>
            </div>
            <Box className={`${styles.fieldGroup} ${styles.section}`}>
              <FormLabel className={styles.sectionLabel}>{t("Account details")}</FormLabel>
              <Box className={styles.inputRow}>
                <TextField
                  name="email"
                  label={t("Email address")}
                  type="email"
                  size="small"
                  sx={{ width: '100%' }}
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  required
                />
                {error && (
                  <div className={styles.errorMessage}>{fieldErrorMessage(error?.email)}</div>
                )}
              </Box>

              <Box className={styles.inputRow}>
                <TextField
                  name="tenantName"
                  label={t("Company name")}
                  size="small"
                  sx={{ width: '100%' }}
                  value={registerData.tenantName}
                  onChange={handleRegisterChange}
                  required
                />
                {error && (
                  <div className={styles.errorMessage}>{fieldErrorMessage(error?.name)}</div>
                )}
              </Box>
            </Box>

            <Box className={`${styles.inputRow} ${styles.radioRow} ${styles.section}`}>
              <FormLabel
                id="subscription-tariff-radio-group"
                error={!!errors.subscriptionTariff}
                className={styles.sectionLabel}
              >
                {t("Subscription tariff")}
              </FormLabel>
              <RadioGroup
                aria-labelledby="subscription-tariff-radio-group"
                aria-describedby={errors.subscriptionTariff ? "subscription-tariff-helper-text" : undefined}
                name="subscriptionTariff"
                value={registerData.subscriptionTariff}
                onChange={handleRegisterChange}
                className={styles.radioGroup}
              >
                {subscriptionTariffs?.map((tariff) => (
                  <FormControlLabel
                    key={tariff.id}
                    value={String(tariff.id)}
                    control={<Radio size="small" />}
                    sx={{ alignItems: "flex-start" }}
                    label={renderTariffLabel(tariff)}
                  />
                ))}
              </RadioGroup>
              {vatExempt ? (
                <Typography variant="caption" color="text.secondary">
                  {t("Seller is exempt from VAT – prices are final.")}
                </Typography>
              ) : (
                vatPercent && (
                  <Typography variant="caption" color="text.secondary">
                    {t("Prices shown are net. {{rate}}% VAT will be added.", { rate: vatPercent })}
                  </Typography>
                )
              )}
              {trialDays ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t("Every plan starts with a free {{days}}-day trial — no charge until it ends.", {
                    days: trialDays,
                  })}
                </Typography>
              ) : null}
              {(errors.subscriptionTariff || error?.subscription_tariff) && (
                <div id="subscription-tariff-helper-text" className={styles.errorMessage}>
                  {errors.subscriptionTariff || fieldErrorMessage(error?.subscription_tariff)}
                </div>
              )}
            </Box>

            {isEnterprise && (
              <Box className={`${styles.inquiryBox} ${styles.section}`}>
                <FormLabel className={styles.sectionLabel}>{t("Enterprise inquiry")}</FormLabel>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "Enterprise pricing is individual. Leave a contact and we will reach out to discuss it."
                  )}
                </Typography>
                <TextField
                  label={t("Phone or email for contact")}
                  size="small"
                  value={inquiryContact}
                  onChange={(event) => setInquiryContact(event.target.value)}
                  error={inquiryContact.trim().length > 0 && !contactValid}
                  helperText={
                    inquiryContact.trim().length > 0 && !contactValid
                      ? t("Enter a valid email address or phone number.")
                      : undefined
                  }
                  fullWidth
                />
                <DatePickerLocalizationProvider>
                  <DateTimePicker
                    label={t("Preferred contact date and time (optional)")}
                    value={inquiryWhen}
                    onChange={(value: Dayjs | null) => setInquiryWhen(value)}
                    disablePast
                    ampm={false}
                    format={DATE_TIME_PICKER_FORMAT}
                    minutesStep={15}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                      actionBar: { actions: ["clear", "accept"] },
                    }}
                  />
                </DatePickerLocalizationProvider>
                {error?.enterprise_inquiry && (
                  <div className={styles.errorMessage}>
                    {fieldErrorMessage(error?.enterprise_inquiry) ??
                      fieldErrorMessage(error?.enterprise_inquiry?.contact)}
                  </div>
                )}
              </Box>
            )}

            <Box className={`${styles.consentsBox} ${styles.section}`}>
              <FormLabel className={styles.sectionLabel}>{t("Consents")}</FormLabel>
              {visibleGroups.map((group) => (
                <FormControlLabel
                  key={group.key}
                  className={styles.consentLabel}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(acceptedGroups[group.key])}
                      onChange={handleGroupToggle(group.key)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {renderConsentLabel(group.documents, group.key)} *
                    </Typography>
                  }
                />
              ))}
              <FormControlLabel
                className={styles.consentLabel}
                control={
                  <Checkbox
                    size="small"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t("I want to receive commercial information about Copigo by email. Optional, can be withdrawn at any time.")}
                  </Typography>
                }
              />
              {(errors.legal || error?.accepted_document_ids) && (
                <div className={styles.errorMessage}>
                  {errors.legal || fieldErrorMessage(error?.accepted_document_ids)}
                </div>
              )}
            </Box>

            <Button
              variant="contained"
              color="success"
              type="submit"
              fullWidth
              disabled={!allGroupsAccepted || !inquiryReady}
              loading={loading}
              className={styles.submitButton}
            >
              {t("Register")}
            </Button>
          </FormControl>
        </form>
      </Box>
      <PublicFooter />
    </Box>
  );
};
